import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ENV_CONFIG } from '../../shared/config/env.config';
import tnLogo from './assets/images/TnLogo.png';
import {
  useLazyCheckSchoolMediumStsQuery,
  useLazyGetAppliedStudentByIdQuery,
  useLazyGetUserPhoneDetailsQuery,
  useBonafideDataCreateMutation,
  type TcStudentRow,
} from './StudentTcDetails.service';

interface Props {
  row: TcStudentRow;
  schoolId: number;
  schoolTypeId: number;
  userName: string;
  selectedClass: string;
  selectedSection: string;
  onBack: () => void;
}

interface BonafideCol {
  field: string;
  header: string;
}
type CertRow = Record<string, string>;

const OTHER_TYPE_SCHOOLS = ['1', '3', '4', '5'];
const CLASS_ROMAN = [
  '',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
];

const StudentBonafide: React.FC<Props> = ({
  row,
  schoolId,
  schoolTypeId,
  userName,
  selectedClass,
  selectedSection,
  onBack,
}) => {
  const toast = useRef<Toast>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const notify = (
    severity: 'success' | 'error' | 'warn' | 'info',
    detail: string
  ): void => toast.current?.show({ severity, detail, life: 5000 });

  const [checkMedium] = useLazyCheckSchoolMediumStsQuery();
  const [getApplied] = useLazyGetAppliedStudentByIdQuery();
  const [getPhone] = useLazyGetUserPhoneDetailsQuery();
  const [bonafideCreate] = useBonafideDataCreateMutation();

  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [validData, setValidData] = useState<Record<string, unknown>>({});
  const [certRows, setCertRows] = useState<CertRow[]>([]);
  const [teacherName, setTeacherName] = useState('');
  const [certUrl, setCertUrl] = useState('');
  const [certNo, setCertNo] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Header columns — aided/self-finance column only for aided/partially-aided schools.
  const headers: BonafideCol[] =
    schoolTypeId === 4 || schoolTypeId === 2
      ? [
          { field: 'class', header: 'Class' },
          { field: 'acYear', header: 'Year of Study' },
          { field: 'schoolName', header: 'Name of the School' },
          { field: 'schoolType', header: 'Type of the School' },
          { field: 'medium', header: 'Medium of Study' },
          {
            field: 'aidedType',
            header:
              'Aided / Self Financed Section(To be filled by Aided Schools Only)',
          },
        ]
      : [
          { field: 'class', header: 'Class' },
          { field: 'acYear', header: 'Year of Study' },
          { field: 'schoolName', header: 'Name of the School' },
          { field: 'schoolType', header: 'Type of the School' },
          { field: 'medium', header: 'Medium of Study' },
        ];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // 1) validate school/medium status
        const medRes = await checkMedium({
          emisId: String(row.NewEmisId ?? ''),
          schoolId,
        }).unwrap();
        if (!medRes.dataStatus || !medRes.result?.length) {
          if (!cancelled) notify('warn', medRes.message ?? 'Not eligible');
          if (!cancelled) setLoading(false);
          return;
        }
        const vData = { ...medRes.result[0] } as Record<string, unknown>;
        vData.parentName =
          vData.father_name || vData.mother_name || vData.guardian_name || '';
        vData.address = [
          vData.house_address,
          vData.street_name,
          vData.area_village,
          vData.pin_code,
        ]
          .filter(Boolean)
          .join(', ');
        if (cancelled) return;
        setValidData(vData);

        // 2) HM / teacher details (signature name)
        const phoneRes = await getPhone({
          userId: userName,
          userType: 1,
        }).unwrap();
        if (!phoneRes.dataStatus || !phoneRes.result?.length) {
          if (!cancelled)
            notify(
              'error',
              'There are no HM details found for this school. Please ensure the HM or In-charge HM details are correct.'
            );
          if (!cancelled) setLoading(false);
          return;
        }
        const hm = phoneRes.result[0] as Record<string, unknown>;
        if (!cancelled)
          setTeacherName(`${hm.teacher_name ?? ''} - ${hm.teacher_id ?? ''}`);

        // 3) applied-student history → build the certificate rows
        const appliedRes = await getApplied({
          sadId: String(vData.sad_id ?? ''),
          stuTyp: 99,
        }).unwrap();
        if (!appliedRes.dataStatus || !appliedRes.result?.length) {
          if (!cancelled)
            notify('error', 'There is some error. Please try again!');
          if (!cancelled) setLoading(false);
          return;
        }
        const stu = appliedRes.result[0] as Record<string, unknown>;
        const logData: Record<string, unknown> = { ...stu };
        const rows: CertRow[] = [];

        const special = (code: string): string | null =>
          code === '1'
            ? 'Other State/Country'
            : code === '3'
              ? 'RTE Direct Admission'
              : code === '4'
                ? 'Private Study'
                : code === '5'
                  ? 'OOSC'
                  : null;

        for (let i = 12; i > 0; i--) {
          const cls = `class${i}`;
          const acYr = `AcYr${i}`;
          const scl = `scl${i}`;
          const schType = `classType${i}`;
          const secType = `classSecType${i}`;
          const medi = `classMed${i}`;
          const classSts = `apr_cls${i}_overall`;

          const code = String(stu[cls] ?? '');
          const matches =
            (code === String(schoolId) || OTHER_TYPE_SCHOOLS.includes(code)) &&
            String(stu[classSts] ?? '') === '1';

          if (matches) {
            const sp = special(code);
            const schoolTypeLabel = (): string => {
              const t = String(stu[schType] ?? '');
              return t === '1'
                ? 'Government'
                : t === '2'
                  ? 'Fully Aided'
                  : t === '3'
                    ? 'Un-aided'
                    : t === '4'
                      ? 'Partially Aided'
                      : t === '5'
                        ? 'Central Govt'
                        : 'Other';
            };
            const aidedLabel = (): string => {
              const t = String(stu[secType] ?? '');
              return t === '1' ? 'Aided' : t === '2' ? 'Self Finance' : '-';
            };
            rows.push({
              class: CLASS_ROMAN[i] ?? '',
              acYear: sp ?? String(stu[acYr] ?? ''),
              schoolName: sp ?? `${stu[scl] ?? ''} - ${userName}`,
              schoolType: sp ?? schoolTypeLabel(),
              medium: sp ?? String(stu[medi] ?? ''),
              aidedType: sp ?? aidedLabel(),
            });
          } else {
            logData[cls] = 0;
            logData[acYr] = 0;
            logData[scl] = 0;
            logData[schType] = 0;
            logData[secType] = 0;
            logData[medi] = 0;
          }
        }

        if (rows.length === 0) {
          if (!cancelled)
            notify(
              'warn',
              'Kindly verify the school and medium of study details under Approvals -> Verify Student Schools'
            );
          if (!cancelled) setLoading(false);
          return;
        }
        if (!cancelled) setCertRows(rows);

        // Persist context for the e-sign return leg.
        localStorage.setItem(
          'esignBonafideData',
          JSON.stringify({
            ...vData,
            filterClass: selectedClass,
            filterSection: selectedSection,
            sad_id: vData.sad_id,
          })
        );

        // 4) create the bonafide record → QR link + certificate number
        const createRes = await bonafideCreate({ records: logData }).unwrap();
        if (createRes.dataStatus) {
          if (!cancelled) {
            setCertUrl(createRes.qrLink ?? '');
            setCertNo(createRes.certificateNo ?? '');
            setReady(true);
          }
        } else if (!cancelled) {
          notify('error', createRes.message ?? 'Unable to create bonafide');
        }
      } catch {
        if (!cancelled)
          notify('error', 'There is some error. Please try again!');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  // Generate the PDF, send it for e-signing, then hand off to the CDAC e-sign form.
  const handleDownload = async (): Promise<void> => {
    if (!contentRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });
      const pdf = new jsPDF({
        compress: true,
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      });
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(
        imgData,
        'PNG',
        20,
        10,
        (canvas.width / 3) * 0.264583,
        (canvas.height / 3) * 0.264583
      );
      const pdfBlob = pdf.output('blob');

      const formData = new FormData();
      const fileName = `${validData.user_id}.pdf`;
      formData.append('pdf_file', pdfBlob, fileName);
      formData.append('student_id', String(validData.user_id ?? ''));
      formData.append('school_id', String(schoolId));
      formData.append('signatureName', teacherName);
      formData.append('certificateNo', certNo);

      const res = await fetch(`${ENV_CONFIG.g2cUrl}/api/esignBonafideCert`, {
        method: 'POST',
        body: formData,
      }).then((r) => r.json());

      if (res?.dataStatus && res.result) {
        // Hand off to the CDAC e-sign service via a full-page form POST.
        const form = document.createElement('form');
        form.action =
          process.env.REACT_APP_API_environment == 'PRODUCTION'
            ? 'https://esignservice.cdac.in/esign2.1/2.1/form/signdoc'
            : 'https://es-staging.cdac.in/esignlevel2/2.1/form/signdoc';
        form.method = 'POST';
        const addField = (name: string, value: string) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };
        addField('eSignRequest', res.result.signXML);
        addField('aspTxnID', res.result.txn);
        addField('Content-Type', 'application/xml');
        document.body.appendChild(form);
        form.submit();
      } else {
        notify('error', res?.message ?? 'Unable to e-sign the certificate');
      }
    } catch {
      notify('error', 'Unable to generate the certificate. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-4">
        <i className="pi pi-spin pi-spinner me-2" /> Preparing bonafide
        certificate…
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="card p-4">
        <p className="mb-3">Unable to generate the bonafide certificate.</p>
        <Button
          label="Back to list"
          icon="pi pi-arrow-left"
          className="p-button-secondary"
          onClick={onBack}
        />
      </div>
    );
  }

  return (
    <div className="card p-4 student-bonafide">
      <Toast ref={toast} />

      <div className="text-end mb-2">
        <Button
          label="Download"
          icon="pi pi-download"
          className="p-button-success me-2"
          loading={downloading}
          onClick={handleDownload}
        />
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary"
          onClick={onBack}
        />
      </div>

      <div className="fontWe dialog-container mx-auto" ref={contentRef}>
        <img src={tnLogo} alt="Watermark" className="watermark" />
        <div className="dialog-content">
          <div className="text-center mb-1">
            <img src={tnLogo} className="tnLogo" alt="TN Logo" />
          </div>
          <div className="text-center" style={{ fontSize: 18 }}>
            <h5>
              <b>BONAFIDE CERTIFICATE</b>
            </h5>
            <span>
              [Ref. : Tamil Nadu Admission to Undergraduate Courses on
              preferential basis to students of Government Schools Act, 2021
              (Tamil Nadu Act No.14 of 2021).]
            </span>
          </div>
          <div className="mt-3" style={{ textAlign: 'justify', fontSize: 18 }}>
            &nbsp;&nbsp;&nbsp;&nbsp; This is to certify that Selvan/Selvi.{' '}
            <b>
              {String(validData.student_name ?? '')} -{' '}
              {String(validData.user_id ?? '')}
            </b>{' '}
            S/o or D/o. <b>{String(validData.parentName ?? '')}</b> residing at{' '}
            <b>{String(validData.address ?? '')}</b> has studied in below
            details
          </div>
          <div className="my-3">
            <DataTable
              value={certRows}
              className="fontWe"
              showGridlines
              tableStyle={{ minWidth: '100%' }}
            >
              {headers.map((col) => (
                <Column key={col.field} field={col.field} header={col.header} />
              ))}
            </DataTable>
          </div>
          <div style={{ fontSize: 18 }}>
            <p className="ms-2" style={{ textAlign: 'justify' }}>
              Verified the above information in respect of the school last
              studied, year of study, medium of instruction and section type
              with the records and found correct.
            </p>
          </div>
          <div className="row mt-4" style={{ fontSize: 18 }}>
            <div className="col-md-8">
              <p>
                Certificate No. <b>{certNo}</b>
              </p>
            </div>
            <div className="col-md-4 text-end qr-content">
              {certUrl && <QRCodeCanvas value={certUrl} size={150} level="H" />}
            </div>
          </div>
          <div className="text-end" style={{ fontSize: 12 }}>
            <p>
              DATA SOURCED FROM TAMIL NADU SCHOOL EDUCATION DEPARTMENT EMIS
              PORTAL
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentBonafide;
